import { useInfiniteQuery } from "@tanstack/react-query";
import { FilterDefinition, RuleGroup } from "@monolith-forensics/monolith-ui";

import AcquisitionsAPI from "@/api/acquisitions/index.js";
import { Acquisition } from "./acquisitions.types";
import {
  AcquisitionCondition,
  ruleGroupToConditions,
} from "./utils/ruleGroupToConditions";

export type GetAcquisitionsQuery = {
  storage_id?: number;
  case_id?: number;
  evidence_id?: number;
  person_id?: number;
  contact_id?: number;
  user_id?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  filter?: RuleGroup;
};

export type GetAcquisitionsResponse = {
  data: Acquisition[];
  total: number;
  pageCount: number;
  currentPage: number;
  nextPage: number | null;
  perPage: number;
};

/*
    The body of POST /acquisitions/query. Unlike the newer endpoints,
    everything must be wrapped in a `query` key and filters must be
    legacy conditions ANDed together (see ruleGroupToConditions).
*/
export type AcquisitionsQueryBody = {
  storage_id?: number;
  case_id?: number;
  evidence_id?: number;
  person_id?: number;
  contact_id?: number;
  user_id?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  order?: { field: string; sort: "asc" | "desc" };
  conditions?: AcquisitionCondition[];
};

// Keys are prefixed "acquisitions:list" so existing invalidations
// (e.g. AcquisitionFlyout modals) refresh this table too.
const acquisitionsQueryKeys = {
  base: ["acquisitions:list"],
  infiniteList: (body: AcquisitionsQueryBody) => [
    "acquisitions:list",
    "infinite",
    body,
  ],
};

export const resolveAcquisitionsQueryBody = (
  query: GetAcquisitionsQuery,
  filterDefinitions?: FilterDefinition[]
): AcquisitionsQueryBody => {
  const { filter, sort_by, sort_dir, search, page, ...scope } = query;

  return {
    ...scope,
    conditions: ruleGroupToConditions(filter, filterDefinitions),
    search: search === "" ? undefined : search,
    order: sort_by
      ? { field: sort_by, sort: sort_dir === "desc" ? "desc" : "asc" }
      : undefined,
  };
};

const useGetInfiniteAcquisitions = (
  query: GetAcquisitionsQuery,
  filterDefinitions?: FilterDefinition[],
  options?: { enabled?: boolean }
) => {
  const body = resolveAcquisitionsQueryBody(query, filterDefinitions);

  return useInfiniteQuery<GetAcquisitionsResponse>({
    queryKey: acquisitionsQueryKeys.infiniteList(body),
    queryFn: async ({ pageParam }) => {
      const res = await AcquisitionsAPI.query({
        query: { ...body, page: pageParam as number },
      });
      // The API layer never rejects: HTTP/network failures resolve as
      // { success: false, error } (connection.js handleError) and a failed
      // server-side query resolves as a 200 empty body. Convert both to real
      // errors here — otherwise react-query records success and the table
      // renders a silent empty result set with the retry UI unreachable.
      if (!res || res.success === false || !Array.isArray(res.data)) {
        throw new Error("Failed to load acquisitions");
      }
      return res;
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? null,
    initialPageParam: 1,
    placeholderData: (data) => data,
    ...options,
  });
};

export const AcquisitionsQuery = {
  queryKeys: acquisitionsQueryKeys,
  useGetInfiniteAcquisitions,
};
